/**
 * A task's checklist — Aaron, Oct 25, note 1.3 ("A checklist of individually
 * checkable items that when checked in addition to a check mark, it strikes
 * through each option … indicate who (of those assigned to the task) should
 * work on each checklist item with their contact circle at the end of each
 * row"); `docs/activity.md` §8.7.
 *
 * Each row: its tick (a check, and the words struck through), its words —
 * renamed in place, Enter or leaving the box saves, Escape puts them back —
 * then the face of the one person on the task it is for, and ×. **Add an
 * item** at the foot keeps the focus, so a list is typed one Enter — or one
 * Tab — at a time (Aaron, Oct 26, answer 2: "it should be easier to tab to
 * next new one then click the button each time"): the item is in, the box is
 * empty and still has the focus; an empty Enter or Escape ends, and Shift+Tab
 * goes back up to the last item's words. Enter in an item's words saves them
 * and steps down to the next, the box after the last. Items typed faster than
 * the server answers wait their turn, drawn as they will land, so none is
 * lost or out of order. A row moves by its grip: dragged, or with ↑ ↓ while
 * the grip has the focus. Somebody taken off the task keeps their items, their face drawn
 * greyed (*no longer on this task*) until they are put back.
 *
 * Ticking the last item does not finish the task — the task's own circle
 * does that, as in Planner. Every write answers the whole checklist, which
 * is what the rows then draw.
 */
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import {
  CHECKLIST_ITEM_MAX_CHARS, MAX_CHECKLIST_ITEMS, checklistWords, type ChecklistAnswer, type ChecklistItem,
  type ChecklistProgress,
} from '@rowted/shared'
import { Popover } from '../../components/overlays.js'
import { cn } from '../../lib/cn.js'
import { usePhone } from '../../lib/use-narrow.js'
import { activityRequest } from '../api.js'
import { Face, type FacePerson } from '../faces.js'
import { Glyph } from '../glyphs.js'

/** A card's and a row's progress, as Planner prints it: the checklist glyph and *2/5*. */
export function ChecklistBadge({ progress, size = 'card' }: { progress: ChecklistProgress | null; size?: 'card' | 'row' }) {
  if (!progress || progress.total === 0) return null
  const all = progress.done === progress.total
  return (
    <span data-checklist={checklistWords(progress)} title={`${progress.done} of ${progress.total} checklist items done`}
      className={cn('inline-flex shrink-0 items-center gap-1 whitespace-nowrap font-semibold',
        size === 'card' ? 'h-[22px] rounded-[6px] px-1 text-[11.5px]' : 'text-[11.5px]',
        all ? 'text-accent-dark' : 'text-text-muted')}>
      <Glyph name="checklist" size={size === 'card' ? 12 : 12} />
      {checklistWords(progress)}
    </span>
  )
}

/** The face of somebody taken off the task: their initials, dashed and faint (§8.7). */
function GoneFace({ person, size }: { person: { name: string; initials: string }; size: number }) {
  return (
    <span title={`${person.name} — no longer on this task`}
      className="inline-flex shrink-0 items-center justify-center rounded-full border border-dashed border-line-strong bg-surface font-bold text-text-faint"
      style={{ width: size, height: size, fontSize: size < 22 ? 8.5 : 9.5 }}>
      {person.initials}
    </span>
  )
}

/** A task with no checklist yet. */
export const NO_CHECKLIST: ChecklistAnswer = { items: [], progress: { done: 0, total: 0 } }

const errorOf = (cause: unknown): string => (cause instanceof Error ? cause.message : String(cause)) || 'That didn’t save.'

export function TaskChecklist({
  taskId, initial, people, readOnly = false, onChanged,
}: {
  taskId: string
  initial: ChecklistAnswer
  /** The task's people, first first — who an item may be for. */
  people: readonly FacePerson[]
  /** A finished task's checklist is only read (Board 19 D keeps a done sheet short). */
  readOnly?: boolean
  /** A write landed: the Board and the List draw the progress again. */
  onChanged?: () => void
}) {
  const phone = usePhone()
  const [items, setItems] = useState<ChecklistItem[]>(initial.items)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState('')
  /* Items typed and on their way, in the order they were typed (answer 2): one request at a time. */
  const [pending, setPending] = useState<{ key: number; title: string }[]>([])
  const queue = useRef<Promise<unknown>>(Promise.resolve())
  const pendingKey = useRef(0)
  const addBox = useRef<HTMLInputElement>(null)
  const wordsAt = useRef(new Map<string, HTMLTextAreaElement>())
  const [picking, setPicking] = useState<{ item: ChecklistItem; anchor: HTMLElement } | null>(null)
  const [drag, setDrag] = useState<{ id: string; to: number } | null>(null)
  const rowsAt = useRef(new Map<string, HTMLDivElement>())
  const grips = useRef(new Map<string, HTMLButtonElement>())
  const base = `/activity/tasks/${taskId}/checklist`

  /* A reload of the sheet brings the server's checklist; the rows draw it. */
  useEffect(() => { setItems(initial.items) }, [initial])

  const send = async (path: string, method: 'POST' | 'PATCH', body: Record<string, unknown>, before?: ChecklistItem[]) => {
    try {
      setError(null)
      const answer = await activityRequest<ChecklistAnswer>(path, { method, body })
      setItems(answer.items)
      onChanged?.()
      return true
    } catch (cause) {
      setError(errorOf(cause))
      /* What the server holds now — somebody else may have changed it too; else as it was before this. */
      activityRequest<ChecklistAnswer>(base)
        .then((answer) => setItems(answer.items))
        .catch(() => { if (before) setItems(before) })
      return false
    }
  }

  const tick = (item: ChecklistItem) => {
    const before = items
    setItems(items.map((one) => (one.id === item.id ? { ...one, done: !one.done } : one)))
    void send(`${base}/${item.id}`, 'PATCH', { done: !item.done }, before)
  }
  const rename = (item: ChecklistItem, title: string) => {
    const words = title.trim()
    if (!words || words === item.title) return
    const before = items
    setItems(items.map((one) => (one.id === item.id ? { ...one, title: words } : one)))
    void send(`${base}/${item.id}`, 'PATCH', { title: words }, before)
  }
  const assign = (item: ChecklistItem, person: FacePerson | null) => {
    setPicking(null)
    void send(`${base}/${item.id}`, 'PATCH', { assigneeId: person?.id ?? null })
  }
  const remove = (item: ChecklistItem) => {
    const before = items
    setItems(items.filter((one) => one.id !== item.id))
    void send(`${base}/${item.id}/remove`, 'POST', {}, before)
  }
  /*
   * The box's words go in after whatever is still on its way, so three typed
   * one Enter after another land as three, in that order — two requests at
   * once could answer the wrong way round and the later answer is the list
   * the rows draw. A refused one comes back into the box if it is empty.
   */
  const add = () => {
    const words = adding.trim()
    if (!words || items.length + pending.length >= MAX_CHECKLIST_ITEMS) return
    setAdding('')
    const key = ++pendingKey.current
    setPending((was) => [...was, { key, title: words }])
    window.setTimeout(() => addBox.current?.scrollIntoView?.({ block: 'nearest' }), 0)
    queue.current = queue.current.then(async () => {
      const ok = await send(base, 'POST', { title: words })
      setPending((was) => was.filter((one) => one.key !== key))
      if (!ok) setAdding((now) => now || words)
    })
  }

  /** The words of the item after this one, or the box after the last (Enter in an item's words). */
  const focusAfter = (item: ChecklistItem) => {
    const at = items.findIndex((one) => one.id === item.id)
    const next = items[at + 1]
    focusWords(next ? wordsAt.current.get(next.id) : addBox.current)
  }

  /** The new-item box from the keyboard (answer 2). */
  const onAddKey = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    const typed = adding.trim().length > 0
    if (event.key === 'Enter') {
      event.preventDefault()
      if (typed) add()
      else event.currentTarget.blur()
      return
    }
    if (event.key === 'Tab' && !event.shiftKey && typed) {
      event.preventDefault()
      add()
      return
    }
    if (event.key === 'Tab' && event.shiftKey) {
      const last = items.at(-1)
      if (!last) return
      /* Leaving the box puts what it holds in (its blur), once. */
      event.preventDefault()
      focusWords(wordsAt.current.get(last.id))
      return
    }
    if (event.key === 'Escape') {
      /* The first Escape empties the box, the next one ends; the sheet closes on the one after. */
      event.stopPropagation()
      if (adding) setAdding('')
      else event.currentTarget.blur()
    }
  }
  const reorder = (ids: string[], focus?: string) => {
    const before = items
    const byId = new Map(items.map((one) => [one.id, one]))
    setItems(ids.map((id) => byId.get(id)!).filter(Boolean))
    void send(`${base}/order`, 'POST', { ids }, before).then(() => {
      if (focus) window.setTimeout(() => grips.current.get(focus)?.focus(), 0)
    })
  }

  /** ↑ ↓ on a grip moves its row one place. */
  const onGripKey = (item: ChecklistItem, event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
    event.preventDefault()
    const at = items.findIndex((one) => one.id === item.id)
    const to = event.key === 'ArrowUp' ? at - 1 : at + 1
    if (to < 0 || to >= items.length) return
    const ids = items.map((one) => one.id)
    ids.splice(at, 1)
    ids.splice(to, 0, item.id)
    reorder(ids, item.id)
  }

  /** A grip pressed and moved: the other rows make room, and letting go saves the order. */
  const onGripDown = (item: ChecklistItem, event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return
    const middles = items.map((one) => {
      const box = rowsAt.current.get(one.id)?.getBoundingClientRect()
      return { id: one.id, mid: box ? box.top + box.height / 2 : 0 }
    })
    const target = (y: number) => middles.filter((slot) => slot.id !== item.id && slot.mid < y).length
    const y0 = event.clientY
    let started = false
    const move = (next: PointerEvent) => {
      if (!started) {
        if (Math.abs(next.clientY - y0) < 4) return
        started = true
      }
      next.preventDefault()
      const to = target(next.clientY)
      setDrag((current) => (current && current.to === to ? current : { id: item.id, to }))
    }
    const up = (next: PointerEvent) => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
      setDrag(null)
      if (!started) return
      const to = target(next.clientY)
      const ids = items.map((one) => one.id).filter((id) => id !== item.id)
      ids.splice(to, 0, item.id)
      if (ids.join() !== items.map((one) => one.id).join()) reorder(ids)
    }
    window.addEventListener('pointermove', move, { passive: false })
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
  }

  /* The rows in the order a drag would leave them. */
  const shown = (() => {
    if (!drag) return items
    const moving = items.find((one) => one.id === drag.id)
    const rest = items.filter((one) => one.id !== drag.id)
    if (moving) rest.splice(drag.to, 0, moving)
    return rest
  })()
  const progress = { done: items.filter((one) => one.done).length, total: items.length }
  const onTask = new Set(people.map((person) => person.id))

  if (readOnly && items.length === 0) return null

  return (
    <div data-checklist-section>
      <div className="mb-1.5 flex items-center text-[12px] font-semibold text-text-muted">
        Checklist
        {items.length > 0 && <span className="ml-auto font-semibold text-text-faint">{checklistWords(progress)}</span>}
      </div>
      <div className={cn('flex flex-col', items.length > 0 && 'rounded-[10px] border border-line bg-surface')}>
        {shown.map((item) => {
          const assignee = item.assignee
          const here = assignee ? onTask.has(assignee.id) && assignee.onTask : false
          const face = assignee
            ? (here
              ? <Face person={people.find((person) => person.id === assignee.id) ?? assignee} size={phone ? 26 : 22} />
              : <GoneFace person={assignee} size={phone ? 26 : 22} />)
            : null
          return (
            <div key={item.id} data-checklist-item={item.title}
              ref={(node) => { if (node) rowsAt.current.set(item.id, node); else rowsAt.current.delete(item.id) }}
              className={cn('group flex items-center gap-1.5 border-b border-line-faint px-1.5 last:border-b-0',
                phone ? 'min-h-[48px]' : 'min-h-[38px]', drag?.id === item.id && 'bg-hover')}>
              {!readOnly && (
                <button ref={(node) => { if (node) grips.current.set(item.id, node); else grips.current.delete(item.id) }}
                  type="button" aria-label={`Move ${item.title}`} title="Drag, or ↑ ↓, to move"
                  onPointerDown={(event) => onGripDown(item, event)} onKeyDown={(event) => onGripKey(item, event)}
                  className={cn('inline-flex shrink-0 cursor-grab touch-none items-center justify-center rounded text-text-faint hover:text-text-muted focus-visible:text-text',
                    phone ? 'h-9 w-6' : 'h-7 w-5 opacity-0 focus-visible:opacity-100 group-hover:opacity-100')}>
                  <Glyph name="grip" size={13} />
                </button>
              )}
              <button type="button" role="checkbox" aria-checked={item.done} aria-label={item.title}
                disabled={readOnly} onClick={() => tick(item)}
                className="inline-flex shrink-0 items-center justify-center rounded-md p-1 hover:bg-hover disabled:cursor-default disabled:hover:bg-transparent">
                <span aria-hidden className={cn('inline-flex items-center justify-center rounded-[5px]',
                  phone ? 'h-5 w-5' : 'h-[17px] w-[17px]',
                  item.done ? 'bg-accent text-white' : 'border-[1.6px] border-line-strong bg-surface')}>
                  {item.done && <Glyph name="check" size={phone ? 13 : 11} stroke={3} />}
                </span>
              </button>
              {readOnly ? (
                <span className={cn('min-w-0 flex-1 py-1 text-[13px] leading-[1.4]', item.done ? 'text-text-faint line-through' : 'text-text')}>
                  {item.title}
                </span>
              ) : (
                <ItemWords item={item} phone={phone} onSave={(words) => rename(item, words)}
                  onNext={() => focusAfter(item)}
                  boxRef={(node) => { if (node) wordsAt.current.set(item.id, node); else wordsAt.current.delete(item.id) }} />
              )}
              {face && (
                readOnly ? face : (
                  <button type="button" aria-label={`For ${assignee!.name}${here ? '' : ' — no longer on this task'}. Change`}
                    onClick={(event) => setPicking({ item, anchor: event.currentTarget })}
                    className="inline-flex shrink-0 rounded-full">
                    {face}
                  </button>
                )
              )}
              {!face && !readOnly && people.length > 0 && (
                <button type="button" aria-label={`Who should do ${item.title}?`} title="Who should do it"
                  onClick={(event) => setPicking({ item, anchor: event.currentTarget })}
                  className={cn('inline-flex shrink-0 items-center justify-center rounded-full border border-dashed border-line-strong text-text-faint hover:text-text-muted',
                    phone ? 'h-[26px] w-[26px]' : 'h-[22px] w-[22px] opacity-0 focus-visible:opacity-100 group-hover:opacity-100')}>
                  <Glyph name="user" size={12} />
                </button>
              )}
              {!readOnly && (
                <button type="button" aria-label={`Remove ${item.title}`} onClick={() => remove(item)}
                  className={cn('inline-flex shrink-0 items-center justify-center rounded text-text-muted hover:text-text',
                    phone ? 'h-9 w-8' : 'h-7 w-6 opacity-0 focus-visible:opacity-100 group-hover:opacity-100')}>
                  <Glyph name="close" size={13} />
                </button>
              )}
            </div>
          )
        })}
        {/* Typed and on its way: where it will land, its words, nothing to press yet. */}
        {pending.map((one) => (
          <div key={`pending-${one.key}`} data-checklist-pending={one.title} aria-busy="true"
            className={cn('flex items-center gap-1.5 border-b border-line-faint px-1.5 last:border-b-0',
              phone ? 'min-h-[48px]' : 'min-h-[38px]')}>
            <span aria-hidden className={cn('shrink-0', phone ? 'w-6' : 'w-5')} />
            <span aria-hidden className="inline-flex shrink-0 p-1">
              <span className={cn('inline-flex rounded-[5px] border-[1.6px] border-line-strong bg-surface', phone ? 'h-5 w-5' : 'h-[17px] w-[17px]')} />
            </span>
            <span className={cn('min-w-0 flex-1 px-1 py-1 leading-[1.4] text-text-muted', phone ? 'text-[15px]' : 'text-[13px]')}>
              {one.title}
            </span>
          </div>
        ))}
      </div>
      {!readOnly && items.length + pending.length < MAX_CHECKLIST_ITEMS && (
        <label className={cn('mt-1.5 flex items-center gap-2 rounded-lg px-2 text-[13px] text-accent focus-within:bg-field',
          phone ? 'h-11' : 'h-9')}>
          <Glyph name="plus" size={14} />
          <input ref={addBox} aria-label="Add an item" value={adding} maxLength={CHECKLIST_ITEM_MAX_CHARS}
            placeholder="Add an item" onChange={(event) => setAdding(event.target.value)}
            onKeyDown={onAddKey}
            onBlur={() => { if (adding.trim()) add() }}
            className="min-w-0 flex-1 bg-transparent text-text outline-none placeholder:font-semibold placeholder:text-accent" />
        </label>
      )}
      {!readOnly && items.length + pending.length >= MAX_CHECKLIST_ITEMS && (
        <div className="mt-1.5 text-[12px] text-text-muted">A checklist can hold up to {MAX_CHECKLIST_ITEMS} items.</div>
      )}
      {error && <div role="alert" className="mt-1.5 text-[12px] text-danger">{error}</div>}
      {picking && (
        <Popover open anchor={picking.anchor} onClose={() => setPicking(null)} width={240} align="right"
          label="Who should do it" className="py-1.5" layer={70}>
          <div className="px-3 pb-1 pt-0.5 text-[11.5px] text-text-muted">Who on the task should do it</div>
          {people.map((person) => (
            <button key={person.id} type="button" role="menuitemradio" aria-checked={picking.item.assignee?.id === person.id}
              onClick={() => assign(picking.item, person)}
              className={cn('flex w-full items-center gap-2 px-3 text-left text-[13px] hover:bg-hover', phone ? 'h-11' : 'h-9')}>
              <Face person={person} size={20} />
              <span className="flex-1 truncate">{person.name}</span>
              {picking.item.assignee?.id === person.id && <span className="text-accent"><Glyph name="check" size={13} /></span>}
            </button>
          ))}
          {picking.item.assignee && (
            <>
              <div className="my-1 border-t border-line-soft" />
              <button type="button" onClick={() => assign(picking.item, null)}
                className={cn('flex w-full items-center gap-2 px-3 text-left text-[13px] text-text-muted hover:bg-hover', phone ? 'h-11' : 'h-9')}>
                <Glyph name="close" size={13} />Nobody in particular
              </button>
            </>
          )}
        </Popover>
      )}
    </div>
  )
}

/** The focus into a box with the caret after its words, ready to go on typing. */
function focusWords(node: HTMLInputElement | HTMLTextAreaElement | null | undefined): void {
  if (!node) return
  node.focus()
  const end = node.value.length
  node.setSelectionRange(end, end)
}

/**
 * An item's words, edited in place: leaving saves, Enter saves and steps down
 * to the next item (or the new-item box after the last), Escape puts them
 * back. A box that grows with the words, so a long item wraps as it reads
 * rather than running off under the face beside it.
 */
function ItemWords({
  item, phone, onSave, onNext, boxRef,
}: {
  item: ChecklistItem; phone: boolean; onSave: (words: string) => void
  onNext: () => void
  boxRef: (node: HTMLTextAreaElement | null) => void
}) {
  const [words, setWords] = useState(item.title)
  const box = useRef<HTMLTextAreaElement | null>(null)
  useEffect(() => { setWords(item.title) }, [item.title])
  useLayoutEffect(() => {
    const node = box.current
    if (!node) return
    node.style.height = '0px'
    node.style.height = `${node.scrollHeight}px`
  }, [words, phone])
  return (
    <textarea ref={(node) => { box.current = node; boxRef(node) }} rows={1} aria-label={`Checklist item: ${item.title}`}
      value={words} maxLength={CHECKLIST_ITEM_MAX_CHARS}
      onChange={(event) => setWords(event.target.value.replace(/\s*\n\s*/g, ' '))}
      onBlur={() => { if (words.trim()) onSave(words); else setWords(item.title) }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          const here = event.currentTarget
          onNext()
          /* Nowhere to step to (a full checklist has no box): leaving still saves. */
          if (document.activeElement === here) here.blur()
        }
        if (event.key === 'Escape') { event.stopPropagation(); setWords(item.title); window.setTimeout(() => (event.target as HTMLTextAreaElement).blur(), 0) }
      }}
      className={cn('block min-w-0 flex-1 resize-none overflow-hidden rounded-md bg-transparent px-1 py-1 leading-[1.4] outline-none focus:bg-field',
        phone ? 'text-[15px]' : 'text-[13px]', item.done ? 'text-text-faint line-through' : 'text-text')} />
  )
}
